import type { CatalogSelection } from 'modules/datacatalog/types';
import type {
    CategoryWithTableOnly,
    DomainWithFields,
    TableWithFields,
    TreeField,
} from 'modules/decision/pages/Explore/components/Sentence/helpers/treeData';

const isField = (item: unknown): item is TreeField => {
    return (
        item !== null &&
        typeof item === 'object' &&
        'field' in item &&
        'table' in item &&
        'type' in item
    );
};

export const isFieldArray = (item: unknown): item is TreeField[] => {
    return Array.isArray(item) && item.every(isField);
};
const isTable = (item: unknown): item is TableWithFields => {
    return (
        item !== null &&
        typeof item === 'object' &&
        Object.values(item).every((value) => isFieldArray(value))
    );
};
export const findFieldInTree = (
    fieldId: string,
    treeStructure:
        | DomainWithFields
        | TableWithFields
        | CategoryWithTableOnly
        | TreeField[],
    domain: string = 'unclassified',
    category: string = 'unclassified',
): CatalogSelection | null => {
    if (isFieldArray(treeStructure)) {
        const field = treeStructure.find((f) => f.field === fieldId);
        if (!field) return null;

        return field.type === 'table'
            ? {
                  kind: 'table',
                  name: field.field,
                  item: field,
                  fields: treeStructure,
                  domain,
                  category,
              }
            : {
                  kind: 'field',
                  name: field.field,
                  item: field,
                  domain,
                  category,
              };
    }
    if (isTable(treeStructure)) {
        const tableFields = Object.values(treeStructure).flat();
        return findFieldInTree(fieldId, tableFields, domain, category);
    }

    return Object.entries(treeStructure).reduce(
        (acc: CatalogSelection | null, [key, item]) => {
            if (domain === 'unclassified') {
                return findFieldInTree(fieldId, item, key) || acc;
            }
            return findFieldInTree(fieldId, item, domain, key) || acc;
        },
        null,
    );
};

export const countFieldsInDomain = (
    domainData:
        | DomainWithFields
        | TableWithFields
        | CategoryWithTableOnly
        | TreeField[],
): number => {
    if (!domainData) return 0;
    if (isFieldArray(domainData)) return domainData.length;

    return Object.values(domainData).reduce((count, catOrTable) => {
        return count + countFieldsInDomain(catOrTable);
    }, 0);
};

const hasTableTreeItem = (
    domainData: CategoryWithTableOnly | TableWithFields,
    tableName: string,
): boolean => {
    const tableCandidate = domainData[tableName];
    if (isFieldArray(tableCandidate)) {
        return true;
    }

    return Object.values(domainData).some(
        (value) => !isFieldArray(value) && tableName in value,
    );
};

/**
 * Returns the list of node ids to expand in the tree to show the selected item.
 * For a field selection, it expands the domain and the table (if applicable).
 * For a table selection, it only expands the domain (the table row is visible without expanding it).
 */
export const getExpandedNodeIdsForSelection = (
    treeData: DomainWithFields,
    selection: CatalogSelection | null,
): string[] => {
    if (!selection) return [];

    const expandedNodeIds = [selection.domain];
    const domainData = treeData[selection.domain];

    if (!domainData || isFieldArray(domainData)) {
        return expandedNodeIds;
    }

    if (selection.kind === 'table') {
        return expandedNodeIds;
    }

    const tableName = selection.item.table;
    if (!hasTableTreeItem(domainData, tableName)) {
        return expandedNodeIds;
    }

    return [...expandedNodeIds, `${selection.domain}-table-${tableName}`];
};
